/* ==========================================================================
   Hidecraft — facets.js
   Collection/search filtering, sorting and pagination with the Section
   Rendering API. URLs are pushed to history so the back button keeps filters.
   ========================================================================== */

(() => {
  'use strict';

  const { debounce, isDesktop } = window.Hidecraft;
  const VIEW_KEY = 'hc-collection-view';

  class FacetFilters extends HTMLElement {
    static cache = new Map();

    connectedCallback() {
      this.sectionId = this.dataset.sectionId;
      this.onChangeDebounced = debounce(() => this.onFormChange(), 500);

      this.addEventListener('change', (event) => {
        const form = this.form;
        if (!form || event.target.form !== form) return;
        if (event.target.matches('[data-price-input]')) return; // handled on input
        this.onFormChange();
      });

      this.addEventListener('input', (event) => {
        if (event.target.matches('[data-price-input]')) this.onChangeDebounced();
      });

      this.addEventListener('submit', (event) => {
        if (event.target === this.form) {
          event.preventDefault();
          this.onFormChange();
        }
      });

      this.addEventListener('click', (event) => {
        const link = event.target.closest('a[data-facet-link]');
        if (link) {
          event.preventDefault();
          this.renderUrl(new URL(link.href, window.location.origin), true, link.closest('.pagination'));
          return;
        }

        const more = event.target.closest('[data-facets-more]');
        if (more) {
          const list = more.previousElementSibling;
          const expanded = more.getAttribute('aria-expanded') === 'true';
          more.setAttribute('aria-expanded', String(!expanded));
          list?.classList.toggle('is-expanded', !expanded);
          return;
        }

        const view = event.target.closest('[data-view-toggle]');
        if (view) this.setView(view.dataset.viewToggle, true);
      });

      this.onPopState = (event) => {
        if (event.state?.facets === undefined && !window.location.search && !this.lastSearch) return;
        this.renderUrl(new URL(window.location.href), false);
      };
      window.addEventListener('popstate', this.onPopState);

      this.setView(this.readView(), false);
    }

    disconnectedCallback() {
      window.removeEventListener('popstate', this.onPopState);
    }

    get form() {
      return this.querySelector('form.facets__form');
    }

    /* ---------- Rendering ---------- */

    onFormChange() {
      const form = this.form;
      if (!form) return;
      const params = new URLSearchParams();
      for (const [key, value] of new FormData(form).entries()) {
        if (value !== '') params.append(key, value);
      }
      const url = new URL(window.location.pathname, window.location.origin);
      url.search = params.toString();
      this.renderUrl(url, true);
    }

    async renderUrl(url, push, scrollToTop = false) {
      const params = new URLSearchParams(url.search);
      params.delete('section_id');
      const search = params.toString();
      this.lastSearch = search;

      const fetchUrl = `${url.pathname}?${search}${search ? '&' : ''}section_id=${this.sectionId}`;
      const main = this.querySelector('[data-facets-region="grid"]');
      main?.classList.add('is-updating');

      try {
        let html = FacetFilters.cache.get(fetchUrl);
        if (!html) {
          const res = await fetch(fetchUrl);
          if (!res.ok) throw new Error(res.status);
          html = await res.text();
          FacetFilters.cache.set(fetchUrl, html);
        }
        // A newer request started meanwhile.
        if (search !== this.lastSearch) return;
        this.applyHTML(html);
        if (push) window.history.pushState({ facets: search }, '', `${url.pathname}${search ? `?${search}` : ''}`);
        if (scrollToTop) this.scrollIntoView({ behavior: 'smooth', block: 'start' });
      } catch (error) {
        console.warn(error);
        window.location.href = `${url.pathname}?${search}`;
      } finally {
        main?.classList.remove('is-updating');
      }
    }

    applyHTML(html) {
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const activeId = document.activeElement?.id;
      const openGroups = [...this.querySelectorAll('details.facets__group')].reduce((map, el) => {
        map[el.id] = el.open;
        return map;
      }, {});
      const expandedLists = [...this.querySelectorAll('[data-facets-more][aria-expanded="true"]')].map(
        (btn) => btn.closest('details')?.id
      );

      this.querySelectorAll('[data-facets-region]').forEach((region) => {
        const fresh = doc.querySelector(`[data-facets-region="${region.dataset.facetsRegion}"]`);
        if (fresh) region.innerHTML = fresh.innerHTML;
      });

      // Keep the user's open/closed filter groups.
      this.querySelectorAll('details.facets__group').forEach((el) => {
        if (el.id in openGroups) el.open = openGroups[el.id];
        if (expandedLists.includes(el.id)) {
          el.querySelector('[data-facets-more]')?.setAttribute('aria-expanded', 'true');
          el.querySelector('.facets__list')?.classList.add('is-expanded');
        }
      });

      // Sort select lives outside the regions; keep it in sync.
      const freshSort = doc.querySelector('select[name="sort_by"]');
      const sort = this.querySelector('select[name="sort_by"]');
      if (freshSort && sort) sort.value = freshSort.value;

      if (activeId) document.getElementById(activeId)?.focus({ preventScroll: true });
      this.setView(this.readView(), false);
    }

    /* ---------- Grid / list view ---------- */

    readView() {
      try {
        return localStorage.getItem(VIEW_KEY) || 'grid';
      } catch (e) {
        return 'grid';
      }
    }

    setView(view, save) {
      if (!this.querySelector('[data-view-toggle]')) return;
      this.querySelectorAll('.collection__grid').forEach((grid) => (grid.dataset.view = view));
      this.querySelectorAll('[data-view-toggle]').forEach((btn) =>
        btn.setAttribute('aria-pressed', String(btn.dataset.viewToggle === view))
      );
      if (save) {
        try {
          localStorage.setItem(VIEW_KEY, view);
        } catch (e) {
          /* storage blocked */
        }
      }
    }
  }
  customElements.define('facet-filters', FacetFilters);

  /* ---------- Load more / infinite scroll ---------- */

  class LoadMore extends HTMLElement {
    connectedCallback() {
      this.button = this.querySelector('.load-more__button');
      this.button?.addEventListener('click', (event) => {
        event.preventDefault();
        this.load();
      });
      if (this.dataset.mode === 'infinite' && 'IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(
          (entries) => {
            if (entries[0].isIntersecting) this.load();
          },
          { rootMargin: '0px 0px 600px 0px' }
        );
        this.observer.observe(this);
      }
    }

    disconnectedCallback() {
      this.observer?.disconnect();
    }

    async load() {
      if (this.loading || !this.dataset.nextUrl) return;
      this.loading = true;
      this.button?.classList.add('is-loading');

      const sectionId = this.dataset.sectionId;
      const url = new URL(this.dataset.nextUrl, window.location.origin);
      url.searchParams.set('section_id', sectionId);

      try {
        const res = await fetch(url);
        const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
        const grid = document.getElementById(`ProductGrid-${sectionId}`);
        const freshGrid = doc.getElementById(`ProductGrid-${sectionId}`);
        if (grid && freshGrid) {
          const firstNew = freshGrid.firstElementChild;
          grid.append(...freshGrid.children);
          // Move focus to the first new product for keyboard users who pressed the button.
          if (document.activeElement === this.button) firstNew?.querySelector('a')?.focus({ preventScroll: true });
        }
        const next = doc.querySelector('load-more');
        if (next) {
          this.replaceWith(next);
        } else {
          this.remove();
        }
      } catch (error) {
        console.warn(error);
        window.location.href = this.dataset.nextUrl;
      } finally {
        this.loading = false;
        this.button?.classList.remove('is-loading');
      }
    }
  }
  customElements.define('load-more', LoadMore);

  /* Desktop sidebar: the drawer panel is always visible, so never keep it locked. */
  window.addEventListener(
    'resize',
    debounce(() => {
      if (!isDesktop()) return;
      document.querySelectorAll('modal-drawer.facets--sidebar[open]').forEach((drawer) => drawer.close());
    }, 200)
  );
})();
